

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Zap, Target, Waves, Moon } from "lucide-react";

interface EnergyBadgeProps {
  energyPreference: string;
  showIcon?: boolean;
  className?: string;
  percentage?: number | null;
}

export const EnergyBadge: React.FC<EnergyBadgeProps> = ({
  energyPreference,
  showIcon = true,
  className = "",
  percentage = null,
}) => {
  // Map legacy energy types to new terminology
  const normalizeEnergyType = (type: string): string => {
    const lowerType = type?.toLowerCase() || "";
    if (lowerType === "high") return "Power";
    if (lowerType === "moderate") return "Focus";
    if (lowerType === "low") return "Drift";
    if (lowerType === "rest") return "Rest";
    
    // Already using new terminology or unknown
    if (["power", "focus", "drift", "rest"].includes(lowerType)) {
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
    
    return type || "Focus"; // Default to Focus if empty
  };

  const getEnergyIcon = () => {
    switch (energyPreference?.toLowerCase()) {
      case "high":
      case "power":
        return <Zap className="h-3 w-3 mr-1" />;
      case "moderate":
      case "focus":
        return <Target className="h-3 w-3 mr-1" />;
      case "low":
      case "drift":
        return <Waves className="h-3 w-3 mr-1" />;
      case "rest":
        return <Moon className="h-3 w-3 mr-1" />;
      default:
        return <Target className="h-3 w-3 mr-1" />;
    }
  };

  const getEnergyColor = () => {
    switch (energyPreference?.toLowerCase()) {
      case "high":
      case "power":
        return "bg-green-100 text-green-800 border-green-200";
      case "moderate":
      case "focus":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "low":
      case "drift":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "rest":
          return "bg-zinc-50 text-zinc-700 border-zinc-200";
  
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const normalizedEnergy = normalizeEnergyType(energyPreference);

  return (
    <Badge
      variant="outline"
      className={`${getEnergyColor()} flex items-center w-fit ${className}`}
    >
      {showIcon && getEnergyIcon()}
      {normalizedEnergy}
      {percentage != null && (
        <span className="ml-1 text-xs text-black/70">&mdash; {Math.round(percentage)}%</span>
      )}
    </Badge>
  );
};

